import { request } from "../client"
import type { CostBenefitEstimate } from "../cost-benefit"

const performanceNow = () =>
  typeof globalThis.performance?.now === "function" ? globalThis.performance.now() : Date.now()

const durationMs = (startedAt: number) => Math.round(performanceNow() - startedAt)

const logPlanningTiming = (event: string, fields: Record<string, unknown>) => {
  globalThis.console?.info?.(event, fields)
}

export type ContactSequenceTemplateId =
  | "digital_standard"
  | "email_letter_standard"
  | "mobile_message_first"
  | "phone_assisted"
  | "letter_first"

export type AppointmentLetterTodoAssignmentMode = "COLLECTIVE" | "PER_EMPLOYEE"

export type ContactSequenceTemplateOption = {
  id: ContactSequenceTemplateId
  label: string
  description: string
  channels: string[]
}

export type KehrblattPlanningSettings = {
  timezone?: string
  flex_slot_minutes: number
  flex_slots_per_day: number
  customer_appointment_window_delta_minutes: number
  customer_appointment_window_rounding_minutes: number
  appointment_letter_lead_days: number
  appointment_letter_auto_dispatch_enabled?: boolean
  appointment_letter_todo_assignment_mode: AppointmentLetterTodoAssignmentMode
}

export type PlanningSettingsResponse = KehrblattPlanningSettings & {
  appointment_letter_auto_dispatch_enabled_at?: string | null
  appointment_letter_auto_dispatch_disabled_reason?: string | null
  default_contact_sequence_template_id: ContactSequenceTemplateId
  available_contact_sequence_templates: ContactSequenceTemplateOption[]
}

export const DEFAULT_CONTACT_SEQUENCE_TEMPLATE_ID: ContactSequenceTemplateId = "digital_standard"
export const PHONE_ASSISTED_CONTACT_SEQUENCE_TEMPLATE_ID: ContactSequenceTemplateId =
  "phone_assisted"

export const resolvePreferredContactSequenceTemplateId = (
  settings?: PlanningSettingsResponse | null,
): ContactSequenceTemplateId => {
  const configured =
    settings?.default_contact_sequence_template_id ?? DEFAULT_CONTACT_SEQUENCE_TEMPLATE_ID
  const phoneAssistedAvailable = Boolean(
    settings?.available_contact_sequence_templates?.some(
      (template) => template.id === PHONE_ASSISTED_CONTACT_SEQUENCE_TEMPLATE_ID,
    ),
  )

  if (configured === DEFAULT_CONTACT_SEQUENCE_TEMPLATE_ID && phoneAssistedAvailable) {
    return PHONE_ASSISTED_CONTACT_SEQUENCE_TEMPLATE_ID
  }

  return configured
}

export const fetchPlanningSettings = (organizationId: string): Promise<PlanningSettingsResponse> =>
  request<PlanningSettingsResponse>(`/planning/settings/${organizationId}`)

export const updatePlanningSettings = (
  organizationId: string,
  payload: PlanningSettingsResponse | KehrblattPlanningSettings,
) => {
  const body: Record<string, unknown> = {
    flex_slot_minutes: Number(payload.flex_slot_minutes ?? 60),
    flex_slots_per_day: Number(payload.flex_slots_per_day ?? 0),
    customer_appointment_window_delta_minutes: Number(
      payload.customer_appointment_window_delta_minutes ?? 30,
    ),
    customer_appointment_window_rounding_minutes: Number(
      payload.customer_appointment_window_rounding_minutes ?? 0,
    ),
    appointment_letter_lead_days: Number(payload.appointment_letter_lead_days ?? 7),
    appointment_letter_todo_assignment_mode:
      payload.appointment_letter_todo_assignment_mode ?? "COLLECTIVE",
  }

  // Omission is intentional: older callers must not switch automation off.
  if (payload.appointment_letter_auto_dispatch_enabled !== undefined) {
    body.appointment_letter_auto_dispatch_enabled = payload.appointment_letter_auto_dispatch_enabled
  }

  if (
    "default_contact_sequence_template_id" in payload &&
    payload.default_contact_sequence_template_id
  ) {
    body.default_contact_sequence_template_id = payload.default_contact_sequence_template_id
  }

  return request<PlanningSettingsResponse>(`/planning/settings/${organizationId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

export type PlanningUnitContactTarget = {
  property_id: string
  property_unit_id?: string | null
}

export type PlanningUnitContactSummary = {
  customer_id: string
  display_name: string
  role: string
  assignment_scope: "PROPERTY" | "UNIT"
  is_primary: boolean
}

export type PlanningUnitContactContext = {
  property_id: string
  property_unit_id?: string | null
  unit_scope: "BUILDING" | "RESIDENTIAL"
  primary_contact?: PlanningUnitContactSummary | null
  contacts: PlanningUnitContactSummary[]
}

export type ResolvePlanningUnitContactContextsPayload = {
  organization_id: string
  targets: PlanningUnitContactTarget[]
}

export type ResolvePlanningUnitContactContextsResponse = {
  items: PlanningUnitContactContext[]
}

export const resolvePlanningUnitContactContexts = (
  payload: ResolvePlanningUnitContactContextsPayload,
): Promise<ResolvePlanningUnitContactContextsResponse> =>
  request<ResolvePlanningUnitContactContextsResponse>(`/planning/unit-contact-contexts/resolve`, {
    method: "POST",
    body: JSON.stringify(payload),
  })

export type AppointmentPlanningStrategy = "AUTO" | "MANUAL" | "KEEP_EXISTING"
export type AppointmentPlanningManualAppointmentType = "HARD" | "FLEX"
export type FlexAppointmentPlanningMode = "DISABLED" | "ALL" | "EVERY_SECOND"

export type AppointmentPlanningManualAppointmentPayload = {
  start_at: string
  end_at: string
  employee_id: string
  flex_type: "HARD" | "SOFT"
  timeframe_start_at?: string | null
  timeframe_end_at?: string | null
}

export type AppointmentPlanningCommitAppointmentPayload = {
  start_at: string
  end_at: string
  employee_id: string
  flex_type: "HARD" | "SOFT"
  timeframe_start_at: string
  timeframe_end_at: string
}

export type AppointmentPlanningRowBasePayload = {
  client_row_id: string
  planning_strategy?: AppointmentPlanningStrategy
  appointment_employee_id?: string | null
  forced_employee_id?: string | null
  appointment_contact_customer_id?: string | null
  flex_appointment_allowed?: boolean | null
  flex_appointment_mode?: FlexAppointmentPlanningMode | null
  manual_appointment_type?: AppointmentPlanningManualAppointmentType | null
  manual_appointment?: AppointmentPlanningManualAppointmentPayload | null
}

export type AppointmentPlanningPreviewRowPayload = AppointmentPlanningRowBasePayload & {
  work_order_id: string
  expected_active_schedule_event_id?: string | null
}

export type SetAppointmentCommitTarget = {
  kind: "SET"
  appointment: AppointmentPlanningCommitAppointmentPayload
  expected_active_schedule_event_id: string | null
}
export type AppointmentCommitTarget =
  | SetAppointmentCommitTarget
  | {
      kind: "KEEP_EXISTING"
      schedule_event_id: string
    }
export type AppointmentPlanningCommitRowPayload = {
  client_row_id: string
  work_order_id: string
  target: AppointmentCommitTarget
  appointment_contact_customer_id?: string | null
}

export type AppointmentPlanningPreviewPayload = {
  organization_id: string
  rows: AppointmentPlanningPreviewRowPayload[]
  employee_ids: string[]
  start_date?: string | null
  planning_horizon_days?: number | null
  plan_flex_appointments?: boolean
}

export type AppointmentPlanningCommitPayload = Pick<
  AppointmentPlanningPreviewPayload,
  "organization_id" | "employee_ids" | "start_date"
> & {
  rows: AppointmentPlanningCommitRowPayload[]
}

export type AppointmentPlanningPreviewWarning = {
  code: string
  severity: "info" | "warning" | "error"
  message: string
}

export type AppointmentPlanningResolvedAppointmentContact = {
  customer_id?: string | null
  display_name?: string | null
  role?: string | null
  scope?: "UNIT" | "BUILDING" | null
  source: "USER_SELECTED" | "AUTO_DERIVED" | "NONE"
}

export type AppointmentPlanningCustomerAvailabilityMatch = {
  tier: "NO_PREFERENCE" | "INSIDE_WINDOW" | "PARTIAL_OVERLAP" | "NEAR_WINDOW" | "OUTSIDE_WINDOW"
  matched_window_start_at?: string | null
  matched_window_end_at?: string | null
  minutes_outside?: number | null
}

export type AppointmentPlanningRouteAnchorKind =
  | "appointment"
  | "external_block"
  | "internal_block"
  | "absence"
  | "office_start"
  | "office_end"

export type AppointmentPlanningRouteAnchor = {
  schedule_event_id: string | null
  work_order_id?: string | null
  start_at: string | null
  end_at: string | null
  employee_id?: string | null
  address_label?: string | null
  title?: string | null
  distance_km?: number | null
  anchor_kind?: AppointmentPlanningRouteAnchorKind | null
  estimated_travel_minutes?: number | null
  travel_time_estimated?: boolean | null
}

export type AppointmentPlanningPreviewAppointment = {
  start_at: string
  end_at: string
  employee_id: string
  employee_name?: string | null
  flex_type: string
  timeframe_start_at?: string | null
  timeframe_end_at?: string | null
  customer_window_start_at?: string | null
  customer_window_end_at?: string | null
  customer_availability_match?: AppointmentPlanningCustomerAvailabilityMatch | null
  previous_anchor?: AppointmentPlanningRouteAnchor | null
  next_anchor?: AppointmentPlanningRouteAnchor | null
}

export type AppointmentPlanningManualSlotOption = {
  start_at: string
  end_at: string
  employee_id: string
  flex_type: "HARD" | "SOFT"
  timeframe_start_at?: string | null
  timeframe_end_at?: string | null
  label?: string | null
}

export type AppointmentPlanningPreviewRow = {
  commit_target?: AppointmentCommitTarget | null
  client_row_id: string
  status: "planned" | "unplanned"
  appointment?: AppointmentPlanningPreviewAppointment | null
  available_planning_strategies?: AppointmentPlanningStrategy[]
  active_schedule_event?: AppointmentPlanningPreviewAppointment | null
  effective_planning_strategy?: AppointmentPlanningStrategy
  manual_slot_options?: AppointmentPlanningManualSlotOption[]
  resolved_appointment_contact?: AppointmentPlanningResolvedAppointmentContact | null
  warnings: AppointmentPlanningPreviewWarning[]
}

export type AppointmentPlanningRouteProperty = {
  property_id: string
  geo_lat: number | null
  geo_lng: number | null
}

export type AppointmentPlanningPreviewResponse = {
  rows: AppointmentPlanningPreviewRow[]
  route_properties?: AppointmentPlanningRouteProperty[]
  summary: {
    total_rows: number
    planned_rows: number
    unplanned_rows: number
    warning_rows: number
    error_rows: number
  }
  optimization_applied: boolean
  optimization_warning?: string | null
  changed_row_count: number
  cost_benefit?: CostBenefitEstimate | null
}

export type AppointmentPlanningManualFlexSlotOption = {
  slot_id: string
  label: string
  employee_id: string
  employee_name?: string | null
  appointment_start_at: string
  appointment_end_at: string
  timeframe_start_at: string
  timeframe_end_at: string
  duration_minutes: number
  flex_type: "SOFT"
}

export type AppointmentPlanningManualFlexSlotOptionsPayload = {
  organization_id: string
  planning_request: AppointmentPlanningPreviewPayload
  target_client_row_id: string
  target_date: string
  target_employee_id: string
}

export type AppointmentPlanningManualFlexSlotOptionsResponse = {
  target_client_row_id: string
  target_date: string
  target_employee_id: string
  query_key: string
  options: AppointmentPlanningManualFlexSlotOption[]
  warnings: AppointmentPlanningPreviewWarning[]
}

export type AppointmentPlanningRowResult = {
  client_row_id: string
  status: "planned" | "failed"
  work_order_id?: string | null
  schedule_event_id?: string | null
  reason?: string | null
  warnings?: AppointmentPlanningPreviewWarning[]
}

export type AppointmentPlanningCommitResponse = {
  scheduled_events: ScheduleEvent[]
  row_results: AppointmentPlanningRowResult[]
  failed: AppointmentPlanningRowResult[]
  impacted_dates: string[]
  optimize_error?: string | null
}

export type AppointmentPlanningStartRecommendationPayload = {
  organization_id: string
  employee_ids: string[]
}

export type AppointmentPlanningStartRecommendation = {
  default_start_date: string
  recommended_start_date: string | null
  source: "earlier_gap" | "standard_lead"
  timezone: string
}

export const getAppointmentPlanningStartRecommendation = (
  payload: AppointmentPlanningStartRecommendationPayload,
  options: { signal?: AbortSignal } = {},
) =>
  request<AppointmentPlanningStartRecommendation>(
    "/planning/appointment-batch/start-recommendation",
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options.signal,
    },
  )

export const commitAppointmentPlanningBatch = async (payload: AppointmentPlanningCommitPayload) => {
  const startedAt = performanceNow()
  const response = await request<AppointmentPlanningCommitResponse>(
    `/planning/appointment-batch/commit`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  )
  logPlanningTiming("appointment_planning_commit_frontend_timing", {
    row_count: payload.rows.length,
    employee_count: payload.employee_ids.length,
    scheduled_count: response.scheduled_events?.length ?? 0,
    failed_count: response.failed?.length ?? 0,
    duration_ms: durationMs(startedAt),
  })

  return response
}

export class AppointmentPlanningPreviewIntegrityError extends Error {
  constructor() {
    super(
      "Die Planungsvorschau ist unvollständig oder enthält widersprüchliche Zeilen. Bitte laden Sie die Terminvorschau erneut. Bereits gespeicherte Aufträge bleiben erhalten.",
    )
    this.name = "AppointmentPlanningPreviewIntegrityError"
  }
}

export const previewAppointmentPlanningBatch = async (
  payload: AppointmentPlanningPreviewPayload,
  signal?: AbortSignal,
) => {
  const startedAt = performanceNow()
  const response = await request<AppointmentPlanningPreviewResponse>(
    `/planning/appointment-batch/preview`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    },
  )
  const requestedIds = new Set(payload.rows.map((row) => row.client_row_id))
  const responseRows = response?.rows

  if (
    requestedIds.size !== payload.rows.length ||
    !Array.isArray(responseRows) ||
    responseRows.length !== payload.rows.length ||
    new Set(responseRows.map((row) => row?.client_row_id)).size !== requestedIds.size ||
    responseRows.some((row) => !requestedIds.has(row?.client_row_id))
  ) {
    throw new AppointmentPlanningPreviewIntegrityError()
  }

  logPlanningTiming("appointment_planning_preview_frontend_timing", {
    row_count: payload.rows.length,
    employee_count: payload.employee_ids.length,
    planned_count: response.summary?.planned_rows ?? 0,
    failed_count: response.summary?.error_rows ?? 0,
    duration_ms: durationMs(startedAt),
  })

  return response
}

export const listAppointmentPlanningManualFlexSlotOptions = async (
  payload: AppointmentPlanningManualFlexSlotOptionsPayload,
) => {
  const startedAt = performanceNow()
  const response = await request<AppointmentPlanningManualFlexSlotOptionsResponse>(
    `/planning/appointment-batch/manual-flex-slots`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  )
  logPlanningTiming("appointment_planning_manual_flex_slots_frontend_timing", {
    row_count: payload.planning_request.rows.length,
    employee_count: payload.planning_request.employee_ids.length,
    option_count: response.options?.length ?? 0,
    duration_ms: durationMs(startedAt),
  })

  return response
}
